/**
 * Real backend mount paths — mirrors `routes/v1/index.ts` exactly
 * (`FRONTEND-BACKEND-INTEGRATION.md` §2). Every path below is a real,
 * certified, mounted route today; nothing here is speculative. Grouped
 * by bounded context for readability, matching the backend's own
 * module boundary — this is a path registry, not a client (see
 * `http-client.ts` for the actual request execution).
 */
export const API_ENDPOINTS = {
  auth: {
    login: '/auth/login',
    // Phase S1 — completes login after a POST /auth/login response with
    // mfaRequired: true.
    mfaVerify: '/auth/mfa/verify',
    // No /auth/refresh or /auth/logout exists on the real backend today
    // (core/authentication/jwt.service.ts's verifyRefreshToken is a real,
    // unused primitive — `FRONTEND-BACKEND-INTEGRATION.md` §3, and Stage
    // 6C.3's own Technical Debt Register already disclosed this same gap).
    // Intentionally NOT listed here — see AUTHENTICATION-GUIDE.md for how
    // the frontend degrades safely in the absence of both.
  },
  patients: '/patients',
  patientCoverages: '/patient-coverages',
  consents: '/consents',
  patientMerges: '/patient-merges',
  recordProtections: '/record-protections',
  identityResolution: '/identity-resolution',
  identityMatchConfig: '/identity-match-config',
  registrationConfig: '/registration-config',

  appointments: '/appointments',
  checkIn: '/appointments',
  queue: '/queue',
  waitlist: '/waitlist',
  slots: '/slots',
  reminders: '/reminders',
  doctorSchedule: '/doctor-schedule',
  doctorLeave: '/doctor-leave',
  doctorAvailabilityOverride: '/doctor-availability/override',

  financial: {
    payments: '/financial/payments',
    receipts: '/financial/receipts',
    advanceDepositAccounts: '/financial/advance-deposit-accounts',
    refunds: '/financial/refunds',
    paymentCorrections: '/financial/payment-corrections',
    receiptCorrections: '/financial/receipt-corrections',
    patientCorrections: '/financial/patient-corrections',
    correctionWorkflow: '/financial/correction-workflow',
    tariffs: '/financial/tariffs',
    packages: '/financial/packages',
    /** ADR-047 — recurring charge rules + accrual generation. */
    recurringCharges: '/financial/recurring-charges',
    discountRules: '/financial/discount-rules',
    postingEvents: '/financial/posting-events',
    approvalChains: '/financial/approval-chains',
    billing: '/financial/billing',
    // Stage 13 — Enterprise Accounting Engine (Milestone 1)
    chartOfAccounts: '/financial/chart-of-accounts',
    journalEntries: '/financial/journal-entries',
    generalLedger: '/financial/general-ledger',
    trialBalance: '/financial/trial-balance',
    // Stage 13 — Enterprise Accounting Engine (Milestone 2, Part 1: Accounts Receivable)
    invoices: '/financial/invoices',
    receiptAllocations: '/financial/receipt-allocations',
    arLedger: '/financial/ar/ledger',
    arOutstanding: '/financial/ar/outstanding',
    arAgeing: '/financial/ar/ageing',
    creditHold: '/financial/credit-hold',
    creditNotes: '/financial/credit-notes',
    debitNotes: '/financial/debit-notes',
    // Stage 13 — Enterprise Accounting Engine (Milestone 2, Part 2: Accounts Payable)
    vendorInvoices: '/financial/vendor-invoices',
    vendorPayments: '/financial/vendor-payments',
    paymentAllocations: '/financial/payment-allocations',
    vendorAdvances: '/financial/vendor-advances',
    vendorCreditNotes: '/financial/vendor-adjustments/credit-notes',
    vendorDebitNotes: '/financial/vendor-adjustments/debit-notes',
    // Vendor Credit Note Workstation — the FinancialAdjustment platform.
    financialAdjustments: '/financial/adjustments',
    apLedger: '/financial/ap/ledger',
    apStatement: '/financial/ap/statement',
    apOutstanding: '/financial/ap/outstanding',
    apAgeing: '/financial/ap/ageing',
    paymentAdvices: '/financial/payment-advices',
    // Stage 13 — Enterprise Accounting Engine (Milestone 2, Part 3: Cash & Bank Management)
    bankAccounts: '/financial/bank-accounts',
    cashCounters: '/financial/cash-counters',
    cashSessions: '/financial/cash-sessions',
    bankDeposits: '/financial/bank-deposits',
    bankWithdrawals: '/financial/bank-withdrawals',
    cashTransfers: '/financial/cash-transfers',
    bankTransfers: '/financial/bank-transfers',
    cheques: '/financial/cheques',
    cashBook: '/financial/cash-book',
    bankBook: '/financial/bank-book',
    cashSummary: '/financial/cash-summary',
    bankSummary: '/financial/bank-summary',
    // Enterprise Architecture Handbook v1.0 — OP Billing Administration, Module 3.
    costCenters: '/financial/cost-centers',
    revenueCenters: '/financial/revenue-centers',
    postingRules: '/financial/posting-rules',
    discountRuleConfigs: '/financial/discount-rule-configs',
    departmentRevenueMappings: '/financial/department-revenue-mappings',
    taxRates: '/financial/tax-rates',
    priceOverrideRules: '/financial/price-override-rules',
    paymentGatewaySettings: '/financial/payment-gateway-settings',
    // Enterprise Architecture Handbook v1.0 — IP Billing Administration, Module 4.
    packageItems: '/financial/packages/items',
    depositRules: '/financial/deposit-rules',
    mlcBillingRules: '/financial/mlc-billing-rules',
    roomTransitionRules: '/financial/room-transition-rules',
    bedReservationSettings: '/financial/bed-reservation-settings',
  },

  clinical: {
    encounters: '/clinical/encounters',
    documents: '/clinical/documents',
    orders: '/clinical/orders',
    patientTimeline: (patientId: string) => `/clinical/patients/${patientId}/timeline`,
    patientWorkflowTimeline: (patientId: string) => `/clinical/patients/${patientId}/workflow-timeline`,
    auditTrail: (entityType: string, entityId: string) => `/clinical/audit/${entityType}/${entityId}`,
    approvalChains: '/clinical/approval-chains',
  },

  masterData: {
    lookupTypes: '/lookup-types',
    lookupTypesByDomain: (domain: string) => `/lookup-types/domain/${domain}`,
    lookupValues: '/lookup-values',
    lookupValuesByType: (lookupTypeId: string) => `/lookup-values/by-type/${lookupTypeId}`,
    lookupValuesImport: '/lookup-values/import/file',
    countries: '/countries',
    states: '/states',
    statesByCountry: (countryId: string) => `/states/by-country/${countryId}`,
    districts: '/districts',
    districtsByState: (stateId: string) => `/districts/by-state/${stateId}`,
    cities: '/cities',
    citiesByState: (stateId: string) => `/cities/by-state/${stateId}`,
    areas: '/areas',
    areasByCity: (cityId: string) => `/areas/by-city/${cityId}`,
    diagnoses: '/diagnoses',
    procedures: '/procedures',
    testMasters: '/test-masters',
    radiologyProcedures: '/radiology-procedures',
    drugMasters: '/drug-masters',
    messageTemplates: '/message-templates',
    genericMasters: '/generic-masters',
    genericMastersImport: '/generic-masters/import/file',
    brandMasters: '/brand-masters',
    brandMastersImport: '/brand-masters/import/file',
    manufacturerMasters: '/manufacturer-masters',
    manufacturerMastersImport: '/manufacturer-masters/import/file',
    kitMasters: '/kit-masters',
    kitMastersImport: '/kit-masters/import/file',
    packagingHierarchyTemplates: '/packaging-hierarchy-templates',
    packagingHierarchyTemplatesImport: '/packaging-hierarchy-templates/import/file',
    vendorPharmacyProfiles: '/vendor-pharmacy-profiles',
    pharmacy: '/pharmacy',
    pharmacyReports: '/pharmacy/reports',
    chargeMasters: '/charge-masters',
    payerMasters: '/payer-masters',
    vendorMasters: '/vendor-masters',
    employeeMasters: '/employee-masters',
    designations: '/designations',
    doctors: '/doctors',
    doctorSpecialties: (doctorId: string) => `/doctors/${doctorId}/specialties`,
    wards: '/wards',
    wardDashboard: (wardId: string) => `/wards/${wardId}/dashboard`,
    nursingStations: '/nursing-stations',
    rooms: '/rooms',
    roomDashboard: (roomId: string) => `/rooms/${roomId}/dashboard`,
    beds: '/beds',
    bedStatusSummary: '/beds/status-summary',
    corporates: '/corporates',
    corporateAgreements: '/corporate-agreements',
    corporateContacts: '/corporate-contacts',
    corporateCreditPolicies: '/corporate-credit-policies',
  },

  procurement: {
    purchaseRequisitions: '/procurement/purchase-requisitions',
    purchaseOrders: '/procurement/purchase-orders',
    stockLocations: '/inventory/stock-locations',
    goodsReceiptNotes: '/procurement/goods-receipt-notes',
    stockBatches: '/inventory/stock-batches',
    stockLedger: '/inventory/stock-ledger',
    stockAdjustments: '/inventory/stock-adjustments',
    stockTransfers: '/inventory/stock-transfers',
    movementTypes: '/inventory/movement-types',
    inventoryMovements: '/inventory/inventory-movements',
    reorderLevels: '/inventory/reorder-levels',
    reports: '/procurement/reports',
  },

  /**
   * General Stores — the consumables/non-drug workstation.
   *
   * `items` points at the SAME `/item-masters` master the rest of the platform
   * uses, and every document the workstation raises goes to the procurement
   * routes above. Only `stock` is stores-specific, because on-hand-by-item was
   * the one question the inventory engine could not already answer.
   */
  generalStores: {
    items: '/item-masters',
    stock: '/general-stores/stock',
    stockBatches: '/general-stores/stock/batches',
    inward: '/general-stores/inward',
    inwardLines: (grnId: string) => `/general-stores/inward/${grnId}/lines`,
    issues: '/general-stores/issues',
    issueType: '/general-stores/issues/type',
    issueLines: (movementId: string) => `/general-stores/issues/${movementId}/lines`,
    reportValuation: '/general-stores/reports/valuation',
    reportConsumption: '/general-stores/reports/consumption',
    reportItemMovement: '/general-stores/reports/item-movement',
    reportExpiring: '/general-stores/reports/expiring',
    requisitions: '/general-stores/requisitions',
    requisitionLines: (id: string) => `/general-stores/requisitions/${id}/lines`,
    purchaseOrders: '/general-stores/purchase-orders',
    purchaseOrderLines: (id: string) => `/general-stores/purchase-orders/${id}/lines`,
  },

  pricing: {
    priceLists: '/pricing/price-lists',
    selectPrice: '/pricing/select-price',
    selectBatch: '/pricing/select-batch',
    reports: '/pricing/reports',
    commercialRules: '/pricing/commercial-rules',
    contracts: '/pricing/contracts',
    tax: '/pricing/tax',
    discountRules: '/pricing/discount-rules',
    promotions: '/pricing/promotions',
  },

  pharmacyBilling: {
    sales: '/pharmacy-billing/sales',
    reports: '/pharmacy-billing/reports',
    creditLedger: '/pharmacy-billing/credit-ledger',
  },

  pharmacyReporting: {
    dayReport: '/pharmacy-reporting/day-report',
  },

  /**
   * Reports & Analytics — the four cross-context operational reports.
   *
   * Each report is a triple: the analytical body, the paged listing behind it,
   * and the export. Named per report rather than as one base string so a call
   * site cannot accidentally build `/analytics/collections/rows` for the doctor
   * report by string-joining the wrong pieces.
   */
  analytics: {
    /** The filter dropdowns, shared by every report and keyed on the date range. */
    filters: '/analytics/filters',
    registrationAppointments: '/analytics/registration-appointments',
    doctorConsultations: '/analytics/doctor-consultations',
    collections: '/analytics/collections',
    /**
     * The Front Desk dashboard. Reachable BOTH as a tab of `/reports` and as the
     * Front Desk's own landing page at `/front-desk/dashboard` — one endpoint,
     * one component, two routes. See `FrontDeskOverviewTab`.
     */
    frontDesk: '/analytics/front-desk',
    /**
     * The IP tab of that same dashboard — admissions where the OP half counts
     * consultations. A SEPARATE ENDPOINT rather than `front-desk?stream=IP`:
     * every field below the date range differs with the unit, so one endpoint
     * answering both would be a union the screen has to narrow before it can
     * read a single figure. See `FrontDeskIpTab`.
     */
    frontDeskIp: '/analytics/front-desk-ip',
    /**
     * The Statistics statement — the dashboard's OP and IP figures together, with
     * department and doctor codes, and the monthly workbook for the government.
     * `/statistics/export` carries PATIENT-LEVEL rows and is gated on its own
     * permission. See `StatisticsTab`.
     */
    statistics: '/analytics/statistics',
  },

  platform: {
    tenants: '/tenants',
    hospitals: '/hospitals',
    branches: '/branches',
    departments: '/departments',
    shifts: '/shifts',
    roles: '/roles',
    users: '/users',
    permissions: '/permissions',
    organizationSettings: '/organization-settings',
    abhaSettings: '/abha-settings',
    systemSettings: '/system-settings',
    localizationSettings: '/localization-settings',
    license: '/licenses',
    systemBackups: '/system-backups',
    sequences: '/sequences',
    approvalMatrices: '/approval-matrices',
    holidays: '/holidays',
    workingHours: '/working-hours',
    bookingChannels: '/booking-channels',
    financialYears: '/financial-years',
    // Phase G1 — Enterprise Administration Portal. Real, mounted, new this stage.
    auditLogs: '/audit-logs',
    // Phase S1 — MFA administration, mirroring the reset-password endpoint shape.
    userMfaStatus: (id: string) => `/users/${id}/mfa/status`,
    userMfaReset: (id: string) => `/users/${id}/mfa/reset`,
    userMfaOverride: (id: string) => `/users/${id}/mfa/override`,
    userMfaRecoveryCodesRegenerate: (id: string) => `/users/${id}/mfa/recovery-codes/regenerate`,
    userMfaTrustedDevice: (id: string, deviceId: string) => `/users/${id}/mfa/trusted-devices/${deviceId}`,
    userMfaTrustedDeviceRevoke: (id: string, deviceId: string) => `/users/${id}/mfa/trusted-devices/${deviceId}/revoke`,
    systemSettingsMfaEmergencyOverride: '/system-settings/mfa/emergency-override',
  },

  // ADR-033 — Enterprise "My Profile" self-service module. Every path is
  // scoped to the caller's own userId server-side (never a route param
  // here) — see backend's user-profile.routes.ts, mounted at /users/me
  // BEFORE /users/:id to avoid a route collision.
  profile: {
    me: '/users/me',
    password: '/users/me/password',
    mfaStatus: '/users/me/mfa/status',
    mfaEnroll: '/users/me/mfa/enroll',
    mfaVerifyEnrollment: '/users/me/mfa/verify-enrollment',
    mfaRecoveryCodesRegenerate: '/users/me/mfa/recovery-codes/regenerate',
    mfaTrustedDevice: (deviceId: string) => `/users/me/mfa/trusted-devices/${deviceId}`,
    sessions: '/users/me/sessions',
    sessionLogout: (id: string) => `/users/me/sessions/${id}/logout`,
    sessionLogoutAll: '/users/me/sessions/logout-all',
    activity: '/users/me/activity',
    documents: '/users/me/documents',
    document: (id: string) => `/users/me/documents/${id}`,
    notificationPreferences: '/users/me/notification-preferences',
    preferencesUi: '/users/me/preferences-ui',
  },
} as const;
